import { useRef, useState } from "react"
import CardFace from "@/components/CardFace/CardFace"
import CardPeek from "@/components/CardFace/CardPeek"
import CardThumb from "@/components/CardFace/CardThumb"
import EmptyState from "@/components/EmptyState/EmptyState"
import JobEditModal from "@/components/JobEditModal/JobEditModal"
import JobStatement from "@/components/JobStatement/JobStatement"
import RoundHeading from "@/components/RoundHeading/RoundHeading"
import Spinner from "@/components/Spinner/Spinner"
import { cardFace } from "@/content/cards"
import { MOSCOW_COPY, PRIORITIES_COPY } from "@/content/priorities"
import { useDebouncedField } from "@/hooks/useDebouncedField"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { clearPlacement, placeCard, setCardNote } from "@/services/jobs"
import { useSessionStore } from "@/store/sessionStore"
import { cardsByBucket, selectJob, selectSession, unplacedCards } from "@/store/selectors"
import { MOSCOW_BUCKETS } from "@/types"
import { useBoardDrag } from "./useBoardDrag"
import { useCardLanding } from "./useCardLanding"
import type { CardId, MoscowBucket } from "@/types"
import styles from "@/styles/pages/Priorities.module.css"

/*
 * The board: one card at a time, out loud, and the facilitator files it.
 *
 * Deliberately not a group activity. The room argues and the facilitator places,
 * which is why `placeCard` is facilitator-only and why the participant view is
 * the same board, live and read-only — watching a card land is the feedback, and
 * six people dragging would turn a decision into a scramble.
 *
 * Click-to-place is the path everything else is layered over, not the fallback:
 * the quadrant IS a button, so a facilitator on a laptop in front of a room, and
 * anyone on a keyboard, reaches the board without a gesture at all.
 *
 * Over that, a card can be dragged — out of the focus slot, and from one
 * quadrant to another once it has landed, because the room changes its mind
 * about a card far more often than it wants to send one back to the queue and
 * re-file it. `useBoardDrag` owns the gesture and explains why it is pointer
 * events rather than HTML5 drag.
 */

interface PrioritiesProps {
  canDrive: boolean
  /** The round is being read back: the board is shown, and nothing moves. */
  readOnly: boolean
}

export default function Priorities({ canDrive, readOnly }: PrioritiesProps) {
  const session = useSessionStore(selectSession)
  const job = useSessionStore(selectJob)
  const { write, isPending } = useFirestoreWrite()
  const [flipped, setFlipped] = useState(false)
  /* Which card the facilitator has pulled back up out of the board. */
  const [revisiting, setRevisiting] = useState<CardId | null>(null)
  const [editing, setEditing] = useState(false)
  /*
   * The reason for a card that has not been filed yet, held locally until it is.
   *
   * It cannot go to Firestore early: `placements` is keyed by card id and the
   * snapshot mapper drops any entry without a valid `bucket`, so a note written
   * on its own would be stored, never read back, and appear to have been lost
   * until the card was placed. So an unplaced card's reason waits here and is
   * written alongside the bucket. Once a card IS placed, the field switches to
   * the normal debounced write — see `ReasonField`.
   */
  const [pending, setPending] = useState<{ cardId: CardId; note: string } | null>(null)

  const focusRef = useRef<HTMLDivElement>(null)
  const boardRef = useRef<HTMLDivElement>(null)

  /*
   * Everything below asks `driving`, not `canDrive`.
   *
   * The room's own view of this round is already the read-only one — they watch
   * a card land — so a facilitator looking back gets exactly that screen rather
   * than a second, differently-broken version of the board. One condition, one
   * layout.
   */
  const driving = canDrive && !readOnly

  /*
   * Derived above the guard below, because the landing animation is a hook and a
   * hook cannot sit under a conditional return. All three read a job that may not
   * be here yet, so all three tolerate null.
   */
  const queue = unplacedCards(job)
  const byBucket = cardsByBucket(job)
  /*
   * The card in the focus slot: the next unplaced one, or one the facilitator has
   * reopened. A revisited card that has since been re-placed falls back to the
   * queue, so the slot never shows something the board no longer holds.
   */
  const focusId = revisiting && job?.cardIds.includes(revisiting) ? revisiting : (queue[0] ?? null)

  /*
   * A card filed into ANY quadrant flies there — the hook diffs the whole set
   * against the last snapshot, so which bucket it landed in does not matter here.
   */
  useCardLanding({
    placed: MOSCOW_BUCKETS.flatMap((bucket) => byBucket[bucket]),
    focusId,
    focusRef,
    boardRef,
  })

  /*
   * Keyed by the bucket being filed to rather than by the card. There is only
   * ever one card in the focus slot, and "which quadrant is this going to" is
   * what the facilitator is waiting to see — the click target is the quadrant.
   */
  const filingTo = (bucket: MoscowBucket) => `place-${bucket}`

  const place = (cardId: CardId, bucket: MoscowBucket) => {
    if (!driving || !session || !job) return
    const note = pending?.cardId === cardId ? pending.note.trim() : ""
    setRevisiting(null)
    setFlipped(false)
    setPending(null)
    write(async () => {
      await placeCard(session.id, job.id, cardId, bucket)
      // Bucket first: the note is only readable once the placement exists.
      if (note) await setCardNote(session.id, job.id, cardId, note)
    }, filingTo(bucket))
  }

  /* Above the guard for the same reason the landing is: a hook cannot sit under
     a conditional return, so it tolerates a job that has not arrived. */
  const drag = useBoardDrag({
    enabled: driving,
    onDrop: (cardId, bucket) => {
      // Put back where it already was. The room decided nothing, so nothing is
      // written — a re-place would spin the quadrant and re-log the same fact.
      if (job?.placements[cardId]?.bucket === bucket) {
        /* It still left the focus slot, though. `place` is what normally closes
           a revisit, and this returns before it: without this the card sits in
           the slot and in its quadrant at once, with no way back out. */
        if (revisiting === cardId) {
          setRevisiting(null)
          setFlipped(false)
        }
        return
      }
      place(cardId, bucket)
    },
  })

  if (!session || !job) return null

  const mustCap = session.config.mustHaveCap
  const overCap = byBucket.must.length > mustCap

  const focusFace = focusId ? cardFace(focusId) : null

  const filing = MOSCOW_BUCKETS.some((bucket) => isPending(filingTo(bucket)))

  return (
    <div className={styles.priorities}>
      <RoundHeading
        title={PRIORITIES_COPY.heading}
        objective={PRIORITIES_COPY.objective}
        status={
          queue.length > 0
            ? PRIORITIES_COPY.remaining(queue.length)
            : overCap
              ? PRIORITIES_COPY.overCap(mustCap)
              : PRIORITIES_COPY.allPlacedTitle
        }
      />

      <JobStatement
        job={job}
        userStoryEnabled={session.config.userStoryEnabled}
        onEdit={driving ? () => setEditing(true) : undefined}
      />
      {editing ? (
        <JobEditModal
          sessionId={session.id}
          job={job}
          userStoryEnabled={session.config.userStoryEnabled}
          onClose={() => setEditing(false)}
        />
      ) : null}

      <div className={styles.layout}>
        <section className={styles.focus}>
          {focusId ? (
            <>
              <div
                ref={focusRef}
                className={styles.focusCard}
                data-grab={driving ? "" : undefined}
                /* The flip button inside the card is not a handle — see the
                   guard in `useBoardDrag`. */
                onPointerDown={drag.grab(focusId)}
                onClickCapture={drag.swallowClick}
                title={driving ? PRIORITIES_COPY.moveHint : undefined}
              >
                <CardFace
                  cardId={focusId}
                  side={flipped ? "back" : "front"}
                  size="lg"
                  onFlip={() => setFlipped((value) => !value)}
                />
              </div>

              {/*
               * The reason is captured against the card before it moves, which is
               * why the hint says so: a card filed without one leaves the report
               * with a name and no argument.
               */}
              {driving ? (
                job.placements[focusId] ? (
                  <ReasonField
                    key={focusId}
                    sessionId={session.id}
                    jobId={job.id}
                    cardId={focusId}
                    value={job.placements[focusId].note}
                  />
                ) : (
                  <label className={styles.reason}>
                    <span className={styles.reasonLabel}>{PRIORITIES_COPY.reasonLabel}</span>
                    <textarea
                      className={styles.reasonInput}
                      placeholder={PRIORITIES_COPY.reasonPlaceholder}
                      value={pending?.cardId === focusId ? pending.note : ""}
                      onChange={(event) =>
                        setPending({ cardId: focusId, note: event.target.value })
                      }
                    />
                    <span className={styles.reasonHint}>{PRIORITIES_COPY.reasonHint}</span>
                  </label>
                )
              ) : (
                <p className={styles.watching}>{focusFace?.statement}</p>
              )}
            </>
          ) : (
            <EmptyState
              title={PRIORITIES_COPY.allPlacedTitle}
              detail={PRIORITIES_COPY.allPlacedBody}
            />
          )}
        </section>

        <div ref={boardRef} className={styles.board}>
          {MOSCOW_BUCKETS.map((bucket) => {
            const ids = byBucket[bucket]
            const hot = drag.over === bucket
            const bucketOverCap = bucket === "must" && overCap

            return (
              <section
                key={bucket}
                /* What `useBoardDrag` hit-tests against. The drop target is the
                   whole quadrant, including the click-to-place button
                   stretched over it. */
                data-bucket={bucket}
                className={[
                  styles.quad,
                  styles[bucket],
                  hot ? styles.quadHot : "",
                  bucketOverCap ? styles.quadOver : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <header className={styles.quadHead}>
                  <span className={styles.quadLabel}>{MOSCOW_COPY[bucket].label}</span>
                  {/*
                    Tinted in the quadrant's own colour rather than the shared
                    Pill, so the head reads as one object. Must-have carries its
                    cap because it is the only count the room is held to.
                  */}
                  <span
                    className={
                      bucketOverCap
                        ? `${styles.quadCount} ${styles.quadCountOver}`
                        : styles.quadCount
                    }
                  >
                    {bucket === "must" ? `${ids.length}/${mustCap}` : ids.length}
                  </span>
                </header>
                <p className={styles.quadHint}>{MOSCOW_COPY[bucket].hint}</p>

                <div className={styles.quadCards}>
                  {ids.map((cardId) => (
                    <span
                      key={cardId}
                      /* Both the flight's destination and, while one is in the
                         air, the tile it is going to become — held down until
                         the ghost is over it. See `CardLanding`. */
                      data-landing={cardId}
                      className={styles.placed}
                      /* A drag that ends back over the tile it started on still
                         fires a click, and that click is the one below, which
                         would pull the card out of the board it was just moved
                         inside of. */
                      onClickCapture={drag.swallowClick}
                    >
                      {/* The eye sits top-left, clear of the send-back button
                          hanging off the top-right corner. */}
                      <CardPeek cardId={cardId}>
                        <CardThumb
                          cardId={cardId}
                          size="sm"
                          ghosted={drag.carrying === cardId}
                          /* A press that does not travel is a click, and pulls
                             the card back up into the focus slot; one that does
                             carries it to another quadrant. */
                          onClick={driving ? () => setRevisiting(cardId) : undefined}
                          onPointerDown={driving ? drag.grab(cardId) : undefined}
                        />
                      </CardPeek>
                      {driving ? (
                        <button
                          type="button"
                          className={styles.unplace}
                          title={PRIORITIES_COPY.sendBack}
                          aria-label={PRIORITIES_COPY.sendBack}
                          disabled={isPending(`unplace-${cardId}`)}
                          aria-busy={isPending(`unplace-${cardId}`) || undefined}
                          onClick={() =>
                            write(
                              () => clearPlacement(session.id, job.id, cardId),
                              `unplace-${cardId}`
                            )
                          }
                        >
                          {isPending(`unplace-${cardId}`) ? <Spinner size="sm" /> : "×"}
                        </button>
                      ) : null}
                    </span>
                  ))}
                </div>

                {/*
                  The footer says the same thing whether the quadrant is empty or
                  full, because it is always true and the board is read from
                  across a room: four quadrants each labelled with their own
                  state ("Empty", "Drop zone", "Drop it here") turned the picture
                  into a list of statuses.
                */}
                <span className={styles.quadFoot}>{PRIORITIES_COPY.dropLabel}</span>

                {/*
                 * Click-to-place: the whole quadrant is the button when there is
                 * a card in the focus slot. Keyboard and touch reach the board
                 * through this, not through drag.
                 */}
                {driving && focusId ? (
                  <button
                    type="button"
                    className={styles.quadDrop}
                    /* The board is one card at a time, so every quadrant waits
                     * while one placement is out. Pressing a second would file
                     * the card the focus slot has not advanced past yet. */
                    disabled={filing}
                    aria-busy={isPending(filingTo(bucket)) || undefined}
                    onClick={() => place(focusId, bucket)}
                  >
                    {/* The button's own label is hidden for the panel behind it,
                        so the spinner needs a colour and a size back. */}
                    {isPending(filingTo(bucket)) ? (
                      <Spinner size="lg" className={styles.quadSpinner} />
                    ) : null}
                    {MOSCOW_COPY[bucket].label}
                  </button>
                ) : null}
              </section>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/*
 * "Why does it belong?" — stored as the placement's note.
 *
 * Keyed by card id at the call site so a pending keystroke lands on the card it
 * was typed against rather than on whichever card the queue advanced to.
 *
 * Writing a note for a card with no placement yet is fine: `setCardNote` patches
 * `placements.<cardId>.note` by dot path, so the reason can be captured before
 * the room has agreed where the card goes — which is the order the round asks
 * for.
 */
function ReasonField({
  sessionId,
  jobId,
  cardId,
  value,
}: {
  sessionId: string
  jobId: string
  cardId: CardId
  value: string
}) {
  const { write } = useFirestoreWrite()
  const field = useDebouncedField({
    value,
    onCommit: (note) => write(() => setCardNote(sessionId, jobId, cardId, note)),
  })

  return (
    <label className={styles.reason}>
      <span className={styles.reasonLabel}>{PRIORITIES_COPY.reasonLabel}</span>
      <textarea
        className={styles.reasonInput}
        placeholder={PRIORITIES_COPY.reasonPlaceholder}
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
      />
      <span className={styles.reasonHint}>{PRIORITIES_COPY.reasonHint}</span>
    </label>
  )
}
